import {ChangeDetectionStrategy, Component, computed, input, InputSignal, Signal} from '@angular/core';
import {CommonModule} from '@angular/common';

import {DrawingModel, EventModel, GameModel, TournamentCategoryModel, TournamentModel} from 'shared/models';
import {
  LeadConversation2Component
} from '../tournaments-categories-events-overview-ui/lead-conversation2-ui/lead-conversation2.component';
import {
  LeadConversationComponent
} from '../tournaments-categories-events-overview-ui/lead-conversation-ui/lead-conversation.component';

interface DrawingsPerGroup {
  eventId: number;
  numberDrawings: number;
  numberOfPlayersToMainDraw: number;
}

@Component({
  selector: 'event-overview-ui',
  standalone: true,
  imports: [CommonModule, LeadConversation2Component, LeadConversationComponent],
  templateUrl: './event-overview-ui.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventOverviewUi {
  tournament: InputSignal<TournamentModel | undefined> = input.required<TournamentModel | undefined>();
  event: InputSignal<EventModel> = input.required<EventModel>();
  drawings: InputSignal<DrawingModel[]> = input.required<DrawingModel[]>();
  games: InputSignal<GameModel[]> = input.required<GameModel[]>();

  private eventsDrawings: Signal<DrawingModel[]> = computed((): DrawingModel[] => {
    const tournament: TournamentModel | undefined = this.tournament();
    if (tournament === undefined) {
      return [];
    }

    const event: EventModel = this.event();

    const tc: TournamentCategoryModel | undefined = tournament.tournamentsCategories
      .find((tc: TournamentCategoryModel): boolean => {
        for (const e of (tc.events || [])) {
          if (e.id === event.id) {
            return true;
          }
        }
        return false;
      });

    if (tc && tc.events) {
      const childrenEventsIds: number[] = tc.events
        .filter((childEvent: EventModel): boolean => {
          return childEvent.parentId === event.id;
        })
        .map((childEvent: EventModel): number => {
          return childEvent.id;
        });

      const eventIds: number[] = [event.id, ...childrenEventsIds];

      const drawings: DrawingModel[] = this.drawings();

      return drawings.filter((drawing: DrawingModel): boolean => {
        return eventIds.includes(drawing.eventId);
      });
    }

    return [];
  });

  protected numberEventsTeams: Signal<number> = computed((): number => {
    const eventsDrawings: DrawingModel[] = this.eventsDrawings();

    let eventsTeamsIds: number[] = [];
    for (const drawing of eventsDrawings) {
      eventsTeamsIds.push(drawing.teamId);
    }

    // Unique
    return [...new Set(eventsTeamsIds)].length;
  });

  /**
   * Hace los conteos de los drawings por grupos
   */
  private drawingsPerGroupsCounts: Signal<DrawingsPerGroup[]> = computed((): DrawingsPerGroup[] => {
    const eventsDrawings: DrawingModel[] = this.eventsDrawings();

    const groupsDrawings: DrawingModel[] = eventsDrawings.filter((drawing: DrawingModel): boolean => {
      // @TODO: usar enum
      return drawing.stage === 'groups';
    });

    const counts: {[key: string]: DrawingsPerGroup} = {};
    let key: string;
    for (const drawing of groupsDrawings) {
      key = `${drawing.eventId}-${drawing.group}`;
      if (!(key in counts)) {
        counts[key] = {
          eventId: drawing.eventId,
          numberDrawings: 0,
          // @TODO: obtenerlo de la config del evento
          numberOfPlayersToMainDraw: 2,
        };
      }
      counts[key].numberDrawings += 1;
    }

    return Object.values(counts);
  });

  /**
   * Calcula el numero de partidos en las fases de grupos
   * Formula de numero de partidos por grupo:
   * N = (n * (n - 1)) / 2
   */
  protected numberGroupsGames: Signal<number> = computed((): number => {
    const drawingsPerGroupsCounts: DrawingsPerGroup[] = this.drawingsPerGroupsCounts();

    let numberGroupsGames: number = 0;
    let n: number;
    for (const item of drawingsPerGroupsCounts) {
      n = item.numberDrawings;
      numberGroupsGames += (n * (n - 1)) / 2;
    }

    return numberGroupsGames;
  });

  /**
   * Hace los conteos de los drawings de los cuadros A
   */
  private drawingsPerMainDrawsCounts: Signal<{eventId: number, numberDrawings: number}[]> = computed((): {eventId: number, numberDrawings: number}[] => {
    const numberDrawingsPerGroups: DrawingsPerGroup[] = this.drawingsPerGroupsCounts();

    const counts: {[key: string]: {eventId: number, numberDrawings: number}} = {};
    let key: number;
    for (const item of numberDrawingsPerGroups) {
      key = item.eventId;
      if (!(key in counts)) {
        counts[key] = {
          eventId: item.eventId,
          numberDrawings: 0,
        };
      }
      counts[key].numberDrawings += item.numberOfPlayersToMainDraw;
    }

    return Object.values(counts);
  });

  /**
   * Calcula el numero de partidos de los cuadros A
   * Formula de numero de partidos por cuadro A:
   * N = n - 1
   */
  protected numberMainDrawsGames: Signal<number> = computed((): number => {
    const drawingsPerMainDrawsCounts: {eventId: number, numberDrawings: number}[] = this.drawingsPerMainDrawsCounts();

    let numberMainDrawsGames: number = 0;
    let n: number;
    for (const item of drawingsPerMainDrawsCounts) {
      n = item.numberDrawings;
      numberMainDrawsGames += n - 1;
    }

    return numberMainDrawsGames;
  });

  /**
   * Hace los conteos de los drawings de los cuadros B
   */
  private drawingsPerBackDrawsCounts: Signal<{eventId: number, numberDrawings: number}[]> = computed((): {eventId: number, numberDrawings: number}[] => {
    const numberDrawingsPerGroups: DrawingsPerGroup[] = this.drawingsPerGroupsCounts();

    const counts: {[key: string]: {eventId: number, numberDrawings: number}} = {};
    let key: number;
    for (const item of numberDrawingsPerGroups) {
      key = item.eventId;
      if (!(key in counts)) {
        counts[key] = {
          eventId: item.eventId,
          numberDrawings: 0,
        };
      }
      counts[key].numberDrawings += item.numberDrawings - item.numberOfPlayersToMainDraw;
    }

    return Object.values(counts);
  });

  /**
   * Calcula el numero de partidos de los cuadros B
   * Formula de numero de partidos por cuadro B:
   * N = n - 1
   */
  protected numberBackDrawsGames: Signal<number> = computed((): number => {
    const drawingsPerBackDrawsCounts: {eventId: number, numberDrawings: number}[] = this.drawingsPerBackDrawsCounts();

    let numberBackDrawsGames: number = 0;
    let n: number;
    for (const item of drawingsPerBackDrawsCounts) {
      n = item.numberDrawings;
      numberBackDrawsGames += n - 1;
    }

    return numberBackDrawsGames;
  });

  private eventsGames: Signal<GameModel[]> = computed((): GameModel[] => {
    const tournament: TournamentModel | undefined = this.tournament();
    if (tournament === undefined) {
      return [];
    }

    const event: EventModel = this.event();

    const tc: TournamentCategoryModel | undefined = tournament.tournamentsCategories
      .find((tc: TournamentCategoryModel): boolean => {
        for (const e of (tc.events || [])) {
          if (e.id === event.id) {
            return true;
          }
        }
        return false;
      });

    if (tc && tc.events) {
      const childrenEventsIds: number[] = tc.events
        .filter((childEvent: EventModel): boolean => {
          return childEvent.parentId === event.id;
        })
        .map((childEvent: EventModel): number => {
          return childEvent.id;
        });

      const eventIds: number[] = [event.id, ...childrenEventsIds];

      const games: GameModel[] = this.games();

      return games.filter((game: GameModel): boolean => {
        return eventIds.includes(game.eventId);
      });
    }

    return [];
  });

  protected numberPlayedGames: Signal<number> = computed((): number => {
    const games: GameModel[] = this.eventsGames();

    const playedGames: GameModel[] = games.filter((model: GameModel): boolean => {
      // @TODO: usar enum
      return model.status === 'finished';
    });

    return playedGames.length;
  });
}
